/* eslint-disable @hakio/no-untranslated-terminology -- flag panel copy is internal (staff-only), not tenant-facing */
import { useMemo, useState } from "react"

import { Button, Select, toUserOptions } from "~/components/design-system/components"
import { useFlagPanel } from "~/features/feature-flags/client/FlagPanelProvider"
import {
  DialogBodyV2,
  DialogContentV2,
  DialogFooterV2,
  DialogHeaderV2,
  DialogRoot,
  DialogTitle,
} from "~/components/reusable/Dialog"
import { onOffLabel } from "~/features/feature-flags/client/model/labels"
import type { FeatureFlagKey } from "~/features/feature-flags/shared/registry"

/**
 * Picks a user and On or Off for them. Picking closes this dialog; the change is then confirmed,
 * with a note, in the apply dialog.
 */
export function AddUserOverrideDialog({
  open,
  onOpenChange,
  flagKey,
  onPick,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Users that already have an override for this flag are left out of the options. */
  flagKey: FeatureFlagKey
  onPick: (userId: string, value: boolean) => void
}) {
  const panel = useFlagPanel()
  const [email, setEmail] = useState<string | null>(null)
  const options = useMemo(() => {
    const existing = new Set(panel.userOverrides(flagKey).map((o) => o.targetId))
    return toUserOptions(panel.users.filter((user) => !existing.has(user.id)))
  }, [panel, flagKey])
  const picked = options.find((option) => option.value === email) ?? null
  const pickedId = panel.users.find((user) => user.email === email)?.id

  const close = (next: boolean) => {
    if (!next) setEmail(null)
    onOpenChange(next)
  }

  return (
    <DialogRoot open={open} onOpenChange={close}>
      <DialogContentV2 className="max-w-lg">
        <DialogHeaderV2>
          <DialogTitle>Add user override</DialogTitle>
        </DialogHeaderV2>
        <DialogBodyV2>
          <p className="paragraph-sm text-fg-subtle max-w-[50ch]">
            A user override beats their company&apos;s setting. Use it to let one person try a
            feature, or to give Hakio staff a gated area inside a customer&apos;s company.
          </p>
          <Select
            aria-label="User"
            placeholder={panel.loadingUsers ? "Loading users" : "Search name or email"}
            isDisabled={panel.loadingUsers}
            options={options}
            value={picked}
            onChange={(option) => setEmail(option?.value ?? null)}
          />
        </DialogBodyV2>
        <DialogFooterV2 className="justify-end gap-2">
          <Button variant="neutral" size="sm" onClick={() => close(false)}>
            Cancel
          </Button>
          {([true, false] as const).map((value) => (
            <Button
              key={String(value)}
              size="sm"
              variant="neutral"
              requires="staff"
              disabled={!pickedId}
              onClick={() => {
                if (!pickedId) return
                close(false)
                onPick(pickedId, value)
              }}
            >
              {onOffLabel(value)}
            </Button>
          ))}
        </DialogFooterV2>
      </DialogContentV2>
    </DialogRoot>
  )
}
